import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { ActionIcon, Box, NavLink, Stack, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { FC, ReactElement, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UserRole } from "../../../models/user.model";

export type HeroIcon = React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement>>;

export interface NavTreeItem {
  id: string;
  label: string;
  icon?: HeroIcon;
  path: string;
  requiredRoles: UserRole[];
  isEnabled?: boolean;
  badge?: string;
  children?: NavTreeItem[];
}

export interface SideNavTreeProps {
  items: NavTreeItem[];
  basePath: string;
  userRole: UserRole | null;
  /** localStorage key for remembering the collapsed state — give each consumer its own
   * (e.g. "cv-portal:reports-nav-collapsed") so Reports and a future Operations Panel
   * migration don't share one flag. */
  storageKey?: string;
}

const DEFAULT_STORAGE_KEY = "cv-portal:sideNavTree-collapsed";
const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 60;

const joinPath = (basePath: string, path: string): string =>
  `${basePath.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;

const isVisible = (item: NavTreeItem, userRole: UserRole | null): boolean =>
  (item.isEnabled ?? true) && userRole !== null && item.requiredRoles.includes(userRole);

const hasActiveDescendant = (item: NavTreeItem, fullPath: string, pathname: string): boolean => {
  if (pathname === fullPath || pathname.startsWith(`${fullPath}/`)) {
    return true;
  }
  return (item.children ?? []).some((child) =>
    hasActiveDescendant(child, joinPath(fullPath, child.path), pathname)
  );
};

/**
 * Generic, presentational left-side tree navigation. Renders `items` (filtered by
 * `requiredRoles`/`isEnabled`) as Mantine `NavLink`s, recursing into `children` when present.
 * Collapsible to an icon-only rail (remembered per-consumer via `storageKey`, and defaults to
 * collapsed on narrow/mobile viewports on first visit).
 *
 * Deliberately has no knowledge of any specific consumer (Reports, Operations, or otherwise) —
 * built for Reports first, but intended to also replace the Operations Panel's flat `Tabs` strip
 * (`OperationsNav.tsx`) in a future, separate change. Do not import anything Reports- or
 * Operations-specific into this file.
 */
export const SideNavTree: FC<SideNavTreeProps> = ({
  items,
  basePath,
  userRole,
  storageKey = DEFAULT_STORAGE_KEY,
}): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      return stored === "true";
    }
    return isMobile ?? false;
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(storageKey, String(next));
      return next;
    });
  };

  const renderItem = (item: NavTreeItem): ReactElement | null => {
    if (!isVisible(item, userRole)) {
      return null;
    }

    const fullPath = joinPath(basePath, item.path);
    const active = hasActiveDescendant(item, fullPath, location.pathname);
    const visibleChildren = (item.children ?? []).filter((child) => isVisible(child, userRole));

    const link = (
      <NavLink
        key={item.id}
        label={collapsed ? undefined : item.label}
        leftSection={item.icon ? <item.icon width={18} /> : undefined}
        active={active}
        color="sopraPurple"
        defaultOpened={active && !collapsed}
        onClick={() => navigate(fullPath)}
        aria-label={item.label}
        style={collapsed ? { justifyContent: "center" } : undefined}
      >
        {!collapsed && visibleChildren.length > 0
          ? visibleChildren.map((child) => renderItem(child))
          : undefined}
      </NavLink>
    );

    if (!collapsed) {
      return link;
    }

    return (
      <Tooltip key={item.id} label={item.label} position="right" withArrow>
        {link}
      </Tooltip>
    );
  };

  return (
    <Box style={{ width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH, transition: "width 0.15s ease" }}>
      <Box style={{ display: "flex", justifyContent: collapsed ? "center" : "flex-end" }} mb={4}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
        >
          {collapsed ? <ChevronRightIcon width={16} /> : <ChevronLeftIcon width={16} />}
        </ActionIcon>
      </Box>
      <Stack gap={2}>{items.map((item) => renderItem(item))}</Stack>
    </Box>
  );
};
